import React, { useState, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';

export const SalaryStructureEditView = ({ handleItemSelect, employeeId }: any) => {
  const activeCompany = useStore(state => state.activeCompany);
  
  const employee = useLiveQuery(
    () => employeeId ? db.employees.get(employeeId) : Promise.resolve(null)
  );

  const allComponents = useLiveQuery(
    () => activeCompany ? db.salaryComponents.where('companyId').equals(activeCompany.id!).toArray() : []
  ) || [];

  const [basicSalary, setBasicSalary] = useState<number | string>('');
  const [selectedComponents, setSelectedComponents] = useState<any[]>([]);

  useEffect(() => {
    if (employee) {
      setBasicSalary(employee.basicSalary || '');
      setSelectedComponents(employee.salaryComponents || []);
    }
  }, [employee]);

  const handleComponentChange = (componentId: number, customAmount: string) => {
    setSelectedComponents(prev => {
      const existing = prev.find(p => p.componentId === componentId);
      if (existing) {
        if (!customAmount) return prev.filter(p => p.componentId !== componentId);
        return prev.map(p => p.componentId === componentId ? { ...p, customAmount: Number(customAmount) } : p);
      }
      if (customAmount) {
         return [...prev, { componentId, customAmount: Number(customAmount) }];
      }
      return prev;
    });
  };

  const handleTogglePercentage = (componentId: number, isChecked: boolean) => {
    setSelectedComponents(prev => {
      if (isChecked) {
        if (!prev.find(p => p.componentId === componentId)) {
          return [...prev, { componentId }];
        }
      } else {
        return prev.filter(p => p.componentId !== componentId);
      }
      return prev;
    });
  };

  const handleSave = async () => {
    if (employeeId && employee) {
      await db.employees.update(employeeId, {
        ...employee,
        basicSalary: Number(basicSalary),
        salaryComponents: selectedComponents
      });
      handleItemSelect({ name: 'Quit' });
    }
  };

  if (!employee) return null;

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black pb-4">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Edit Salary Structure</span>
        <span className="w-1/3 text-center">{employee.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span>Define Salary Package</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center">
        <div className="w-[500px] border border-[#aebec8] bg-[#f8f9fa] shadow-sm flex flex-col pt-2 pb-4">
          <div className="px-4 py-2 flex items-center justify-between font-bold border-b border-gray-300">
            <span className="w-[150px]">Basic Salary</span>
            <input type="number" autoFocus value={basicSalary} onChange={e => setBasicSalary(e.target.value)} className="w-[150px] border border-black focus:bg-[#ffd] focus:outline-none px-1 text-right h-[20px]" />
          </div>

          <div className="px-4 py-2 mt-4 font-bold text-[#1e8585] border-b border-gray-300">Allowances / Deductions</div>
          
          <div className="px-4 py-2 flex flex-col space-y-2 max-h-[300px] overflow-y-auto scrollbar-thin">
            {allComponents.length === 0 && <div className="text-gray-500 italic">No components defined. Please create from Deductions & Allowances.</div>}
            
            {allComponents.map(comp => {
              const selected = selectedComponents.find(c => c.componentId === comp.id!);
              return (
                <div key={comp.id} className="flex items-center justify-between">
                  <span className={`w-[200px] font-bold ${comp.type === 'Deduction' ? 'text-red-700' : 'text-green-700'}`}>
                    {comp.name} 
                    {comp.valueType === 'Percentage' ? ` (${comp.percentageValue}% Basic)` : ''}
                  </span>
                  
                  {comp.valueType === 'Percentage' ? (
                     <input 
                       type="checkbox" 
                       checked={!!selected} 
                       onChange={e => handleTogglePercentage(comp.id!, e.target.checked)} 
                       className="w-[150px]"
                     />
                  ) : (
                    <input 
                      type="number" 
                      placeholder="Fixed Amount"
                      value={selected?.customAmount || ''} 
                      onChange={e => handleComponentChange(comp.id!, e.target.value)} 
                      className="w-[150px] border border-black focus:bg-[#ffd] focus:outline-none px-1 text-right h-[20px]" 
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-center">
            <button onClick={handleSave} className="px-6 py-1 bg-[#1e8585] text-white font-bold hover:bg-[#1a7070]">Save Salary Structure</button>
          </div>
        </div>
      </div>
    </div>
  );
};
